import { Link, useParams } from 'react-router-dom';
import Reveal from '../components/Reveal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { BTN_PRIMARY, EYEBROW } from '../components/styles';
import { getIndustry } from '../content/industries';
import NotFound from './NotFound';
import usePageMeta from '../hooks/usePageMeta';
import FaqJsonLd from '../components/FaqJsonLd';

export default function IndustryDetail() {
  const { slug } = useParams();
  const industry = getIndustry(slug);
  usePageMeta(
    industry ? `Stove Safety for ${industry.name} | RangeSafe` : 'Industry | RangeSafe Commercial Solutions',
    industry ? industry.lead : undefined
  );

  if (!industry) return <NotFound />;

  return (
    <main className="pt-16" data-testid="industry-detail">
      <FaqJsonLd sections={industry.faqSections} id={industry.slug} />
      <header
        className="relative overflow-hidden border-b border-line"
        style={industry.heroFullFrame ? { minHeight: '56.25vw' } : undefined}
      >
        <img
          src={industry.image}
          alt=""
          aria-hidden="true"
          data-testid="industry-hero-image"
          className={`absolute inset-0 h-full w-full object-cover ${industry.heroFullFrame ? 'opacity-60' : 'opacity-30'}`}
          style={industry.heroFullFrame ? { objectPosition: '45% 100%' } : undefined}
          width="1600"
          height="900"
        />
        <div
          className={`absolute inset-0 ${industry.heroFullFrame ? 'bg-gradient-to-b from-base/75 via-base/40 to-base/0' : 'bg-gradient-to-t from-base via-base/70 to-base/40'}`}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-shell px-5 pb-20 pt-24 sm:px-8 lg:pb-28 lg:pt-36">
          <p className={EYEBROW}>Industries / {industry.short}</p>
          <h1 className="mt-5 max-w-4xl font-display text-5xl font-bold uppercase leading-[0.92] tracking-tight text-white sm:text-6xl lg:text-7xl">
            {industry.name}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-dim sm:text-lg">{industry.lead}</p>
        </div>
      </header>

      <div className="mx-auto grid max-w-shell gap-14 px-5 pb-10 pt-10 sm:px-8 lg:grid-cols-3 lg:pb-14 lg:pt-14">
        <Reveal className="lg:col-span-2">
          <div className="space-y-6 text-base leading-relaxed text-dim">
            {industry.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          {industry.note && (
            <p className="mt-8 border-l-2 border-accent bg-panel/60 p-4 text-xs leading-relaxed text-mute">{industry.note}</p>
          )}
          {industry.faqSections ? (
            industry.faqSections.map((sec, si) => (
              <div key={sec.title} data-testid={`industry-faq-section-${si + 1}`}>
                <h2 className="mt-14 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
                  {sec.title}
                </h2>
                <Accordion
                  type="single"
                  collapsible
                  className="mt-8 border-t border-line"
                  data-testid={si === 0 ? 'industry-faq-accordion' : `industry-faq-accordion-${si + 1}`}
                >
                  {sec.items.map((f, i) => (
                    <AccordionItem key={f.q} value={`faq-${si}-${i}`} className="border-b border-line" data-testid={`industry-faq-item-${si + 1}-${i + 1}`}>
                      <AccordionTrigger className="gap-6 py-5 hover:no-underline [&>svg]:text-accentText">
                        <span className="flex items-baseline gap-5 text-left">
                          <span className="font-mono text-[10px] tracking-[0.3em] text-accentText">{String(i + 1).padStart(2, '0')}</span>
                          <span className="font-display text-lg font-semibold uppercase leading-tight tracking-tight text-white sm:text-xl">{f.q}</span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pb-2 pl-[2.6rem] pr-2">
                          {(Array.isArray(f.a) ? f.a : [f.a]).map((p) => (
                            <p key={p.slice(0, 40)} className="text-sm leading-relaxed text-dim">
                              {p}
                            </p>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))
          ) : (
            <>
              <h2 className="mt-14 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
                {industry.name} FAQ
              </h2>
              {industry.faqs ? (
                <Accordion type="single" collapsible className="mt-8 border-t border-line" data-testid="industry-faq-accordion">
                  {industry.faqs.map((f, i) => (
                    <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-line" data-testid={`industry-faq-item-${i + 1}`}>
                      <AccordionTrigger className="gap-6 py-5 hover:no-underline [&>svg]:text-accentText">
                        <span className="flex items-baseline gap-5 text-left">
                          <span className="font-mono text-[10px] tracking-[0.3em] text-accentText">{String(i + 1).padStart(2, '0')}</span>
                          <span className="font-display text-lg font-semibold uppercase leading-tight tracking-tight text-white sm:text-xl">{f.q}</span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="pb-2 pl-[2.6rem] pr-2 text-sm leading-relaxed text-dim">{f.a}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <ul className="mt-8 border-t border-line">
                  {industry.questions.map((q, i) => (
                    <li key={q} className="flex items-baseline gap-6 border-b border-line py-5">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-accentText">Q{i + 1}</span>
                      <p className="font-display text-xl font-semibold uppercase tracking-tight text-white sm:text-2xl">{q}</p>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <aside className="border border-line bg-panel p-6 lg:sticky lg:top-24 lg:p-8" data-testid="industry-next-steps">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute">Next steps</p>
            <div className="mt-5 lg:mt-6">
              <Link to={`/quote?industry=${industry.slug}`} className={`${BTN_PRIMARY} w-full`} data-testid="industry-quote-cta">
                Request a Consultation
              </Link>
            </div>
            <p className="mt-8 hidden border-t border-line2 pt-6 text-xs leading-relaxed text-mute lg:block">
              Every inquiry is reviewed by the RangeSafe Commercial Solutions Team — no automated verdicts, no
              obligation.
            </p>
          </aside>
        </Reveal>
      </div>
    </main>
  );
}
